import { Link, useLocation, useNavigate } from "react-router";
import protechallLogo from "../images/protechallLogo.png";

import Button from "./Button";
import { logtoClient } from "../service/Logto";
import Loader from "./Loader";
import useToggle from "../hooks/useToggle";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { IconUserCircle } from "@tabler/icons-react";
import { connect } from "react-redux";
import { RootState } from "../store/root-reducer";
import RouteNames from "../routes/type";
import { FaCheck } from "react-icons/fa6";

interface AccessPortal {
  po_portal_access: boolean;
  wo_portal_access: boolean;
  dealer_portal_access: boolean;
}
interface AssociatedDealer {
  id: string;
  alias: string;
}
interface Organization {
  id: string;
  name: string;
  logo: string;
  can_file_claim: boolean;
  dealer_id: string;
  permissions: string[];
  associated_dealers: AssociatedDealer[];
}
interface HeaderProps {
  isAuthenticated: boolean;
  name: string;
  email: string;
  org_logo: string;
  org_name: string;
  permissions: string[];
  custom_data: AccessPortal;
  associated_dealers: AssociatedDealer[];
}

function Header({
  isAuthenticated,
  name,
  org_logo,
  org_name,
  permissions,
  email,
  custom_data,
  associated_dealers,
}: HeaderProps) {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const navigate = useNavigate();

  const {
    value: isLoading,
    setTrue: startLoader,
    setFalse: stopLoader,
  } = useToggle(false);

  const handleSignOut = async () => {
    startLoader();
    try {
      await logtoClient.signOut(`${window.location.origin}/`);
    } catch (error) {
      stopLoader();
    }
  };

  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
      if (window.innerWidth > 800) {
        setMobileMenu(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const PortalLinks = [
    {
      key: "dealer_portal_access",
      label: "Dealer Portal",
      url: "https://ace2-portal-dealer.myprotectall.com/",
      isCurrentPortal: true,
    },
    {
      key: "wo_portal_access",
      label: "Work Order Portal",
      url: "https://ace2-portal-work-order.myprotectall.com/",
    },
    {
      key: "po_portal_access",
      label: "Purchase Order Portal",
      url: "https://ace2-portal-purchase-order.myprotectall.com/",
    },
  ];

  const accessiblePortal = PortalLinks.filter(
    (portal) => custom_data?.[portal.key]
  );

  return (
    <header className="bg-white py-3 z-10">
      <Loader isLoading={isLoading} />
      <div className="container">
        <div className="flex items-center justify-between gap-4">
          <Link to="/contracts">
            {org_logo.startsWith("http") ? (
              <img src={org_logo} alt="logo" className="max-h-10" />
            ) : org_name ? (
              <div className="flex flex-col">
                <span className="text-black/80 font-bold font-serif text-xl whitespace-nowrap">
                  {org_name}
                </span>
              </div>
            ) : (
              <img src={protechallLogo} alt="logo" />
            )}
          </Link>
          {isAuthenticated && windowSize >= 800 && (
            <div className="flex gap-5 items-center">
              <div className="flex gap-3">
                <Button
                  variant="blue"
                  btnClassName="w-[160px]"
                  onClick={() => navigate(RouteNames.SUBMIT_TICKET)}
                >
                  Submit Ticket
                </Button>
                {permissions.includes("create:contract") && (
                  <Button
                    variant="blue"
                    btnClassName="w-[160px]"
                    onClick={() => navigate(`${RouteNames.ADD_CONTRACT}`)}
                  >
                    Add Contract
                  </Button>
                )}
                {permissions.includes("create:bulk-contract") && (
                  <Button
                    variant="blueTransparent"
                    btnClassName="w-[160px]"
                    onClick={() => navigate(RouteNames.ADD_CONTRACT_FILE)}
                  >
                    Add Contract File
                  </Button>
                )}
              </div>
              <div className="relative">
                <Popover>
                  {({ close }) => (
                    <>
                      <PopoverButton className="block text-sm/6 font-semibold hover:text-black text-gray-500 focus:outline-none">
                        <IconUserCircle size={30} />
                      </PopoverButton>
                      <PopoverPanel
                        transition
                        className="divide-y rounded-lg bg-white shadow-2xl transition duration-200 ease-in-out [--anchor-gap:var(--spacing-5)] data-[closed]:-translate-y-1 data-[closed]:opacity-0 z-10 border border-gray-200 absolute top-[110%] right-0 min-w-[250px]"
                      >
                        <div className="grid gap-y-2 text-sm">
                          <div className="border-b bg-gray-100 p-4 ">
                            <p className="text-xl font-semibold">{org_name}</p>
                            <p className="text-gray-700 text-sm">
                              {name || "User"}
                            </p>
                            <p className="text-gray-700 text-sm">{email}</p>
                          </div>
                          {associated_dealers.length > 1 && (
                            <div className="px-4 py-3 space-y-2 border-b">
                              <p className="text-sm font-semibold text-primary mb-1">
                                Associated Dealers
                              </p>
                              <div className="max-h-[200px] overflow-y-auto space-y-1">
                                {associated_dealers.map((dealer) => (
                                  <div
                                    key={dealer.id}
                                    className="flex items-center justify-between p-2 rounded-md bg-gray-50 text-gray-700"
                                  >
                                    <span className="font-medium truncate max-w-[180px] text-xs">
                                      {dealer.alias}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {accessiblePortal.length > 1 && (
                            <div className="px-4 py-3 space-y-1">
                              <p className="text-sm font-semibold text-primary text mb-1">
                                Switch to Portal
                              </p>
                              {accessiblePortal.map((portal) => (
                                <div
                                  key={portal.key}
                                  className={
                                    portal.isCurrentPortal
                                      ? "flex items-center justify-between text-md text-gray-800 pointer-events-none cursor-default"
                                      : "flex items-center justify-between text-md text-gray-800"
                                  }
                                >
                                  <a
                                    href={portal.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-primary cursor-pointer"
                                    onMouseDown={() => {
                                      close();
                                    }}
                                  >
                                    {portal.label}
                                  </a>
                                  {portal?.isCurrentPortal && (
                                    <span className="text-primary text ml-2">
                                      <FaCheck size={20} />
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="px-4 pb-2">
                            <Button
                              variant="blue"
                              onClick={handleSignOut}
                              btnClassName="h-9 !p-1.5 w-full"
                              isLoading={isLoading}
                              disabled={isLoading}
                            >
                              Sign out
                            </Button>
                          </div>
                        </div>
                      </PopoverPanel>
                    </>
                  )}
                </Popover>
              </div>
            </div>
          )}
          {windowSize < 800 && (
            <>
              <button onClick={() => setMobileMenu(true)}>
                <IconMenu2 />
              </button>
              <div
                className={`${
                  mobileMenu ? "right-0" : "-right-[300px]"
                } transition-all w-[300px] bg-white fixed top-0 h-dvh z-20`}
              >
                <div className="flex flex-col gap-10 px-5 h-full">
                  <button onClick={() => setMobileMenu(false)} className="py-4">
                    <IconX className="ml-auto " />
                  </button>
                  <ul className="grid gap-y-5">
                    <Link
                      onClick={() => setMobileMenu(false)}
                      to={RouteNames.SUBMIT_TICKET}
                      className={`${
                        location.pathname === RouteNames.SUBMIT_TICKET
                          ? "text-gray-900 underline underline-offset-4"
                          : "text-gray-500"
                      }`}
                    >
                      <li>Submit Ticket</li>
                    </Link>
                    <Link
                      onClick={() => setMobileMenu(false)}
                      to={`${RouteNames.ADD_CONTRACT}`}
                      className={`${
                        location.pathname === RouteNames.ADD_CONTRACT
                          ? "text-gray-900 underline underline-offset-4"
                          : "text-gray-500"
                      }`}
                    >
                      <li>Add Contract</li>
                    </Link>
                    <Link
                      onClick={() => setMobileMenu(false)}
                      to={RouteNames.ADD_CONTRACT_FILE}
                      className={`${
                        location.pathname === RouteNames.ADD_CONTRACT_FILE
                          ? "text-gray-900 underline underline-offset-4"
                          : "text-gray-500"
                      }`}
                    >
                      <li>Add Contract File</li>
                    </Link>
                  </ul>
                  <Button
                    variant="black"
                    btnClassName="w-full mt-auto mb-[30px]"
                    onClick={handleSignOut}
                  >
                    Log Out
                  </Button>
                </div>
              </div>
            </>
          )}
          {mobileMenu && (
            <div
              className={` w-screen bg-black/50 fixed inset-0 h-dvh z-[19]`}
            ></div>
          )}
        </div>
      </div>
    </header>
  );
}
const mapStateProps = (state: RootState) => {
  return {
    name: state?.user?.name,
    email: state?.user?.email,
    org_logo: state.user.selectedOrg.logo || "",
    org_name: state.user.selectedOrg.name || "",
    permissions: state.user.selectedOrg.permissions || [],
    custom_data: state.user.custom_data || [],
    associated_dealers: state.user.selectedOrg.associated_dealers || [],
  };
};

export default connect(mapStateProps)(Header);
